import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaLaptopCode,
  FaMobileAlt,
  FaCloud,
  FaShieldAlt,
  FaChartLine,
  FaUsers,
  FaSearch,
  FaPaintBrush,
  FaCogs
} from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      icon: <FaLaptopCode className="text-4xl text-primary-600" />,
      title: 'Web Development',
      description: 'Custom websites and web applications built with modern frameworks like React, Node.js, and more. We create responsive, high-performance solutions tailored to your business needs.',
      features: ['Responsive Design', 'Progressive Web Apps', 'E-commerce Platforms', 'CMS Integration']
    },
    {
      icon: <FaMobileAlt className="text-4xl text-primary-600" />,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile apps for iOS and Android. We deliver seamless user experiences with cutting-edge technologies like React Native and Flutter.',
      features: ['iOS & Android Apps', 'Cross-platform Development', 'App Store Deployment', 'Push Notifications']
    },
    {
      icon: <FaCloud className="text-4xl text-primary-600" />,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure, migration, and management. We help you leverage AWS, Azure, and Google Cloud to optimize performance and reduce costs.',
      features: ['Cloud Migration', 'Serverless Architectures', 'DevOps Automation', 'Scalable Databases']
    },
    {
      icon: <FaShieldAlt className="text-4xl text-primary-600" />,
      title: 'Cybersecurity',
      description: 'Comprehensive security assessments, penetration testing, and ongoing protection to safeguard your digital assets from evolving threats.',
      features: ['Security Audits', 'Penetration Testing', 'Compliance (GDPR, HIPAA)', '24/7 Monitoring']
    },
    {
      icon: <FaChartLine className="text-4xl text-primary-600" />,
      title: 'Digital Marketing',
      description: 'Data-driven marketing strategies that boost your online presence, generate leads, and increase conversions.',
      features: ['SEO / SEM', 'Social Media Marketing', 'Content Marketing', 'Email Campaigns']
    },
    {
      icon: <FaUsers className="text-4xl text-primary-600" />,
      title: 'IT Consulting',
      description: 'Expert guidance to align your technology strategy with business goals. We help you make informed decisions and adopt the right tools.',
      features: ['Technology Roadmaps', 'Vendor Selection', 'Process Optimization', 'Digital Transformation']
    },
    {
      icon: <FaSearch className="text-4xl text-primary-600" />,
      title: 'SEO & Analytics',
      description: 'Improve your search rankings and understand your audience with advanced analytics and reporting.',
      features: ['Keyword Research', 'On-page SEO', 'Technical SEO', 'Conversion Rate Optimization']
    },
    {
      icon: <FaPaintBrush className="text-4xl text-primary-600" />,
      title: 'UI/UX Design',
      description: 'User-centered design that delights and engages. We create intuitive interfaces and seamless experiences.',
      features: ['Wireframing', 'Prototyping', 'User Research', 'Usability Testing']
    },
    {
      icon: <FaCogs className="text-4xl text-primary-600" />,
      title: 'Custom Software Development',
      description: 'Tailored software solutions to automate processes, integrate systems, and solve unique business challenges.',
      features: ['Enterprise Software', 'API Integration', 'Legacy Modernization', 'IoT Solutions']
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Discovery',
      description: 'We dive deep into your business goals, target audience, and technical requirements.'
    },
    {
      step: '2',
      title: 'Planning',
      description: 'Our team creates a detailed roadmap, technology stack, and project timeline.'
    },
    {
      step: '3',
      title: 'Design',
      description: 'We craft intuitive designs and prototypes for your feedback and approval.'
    },
    {
      step: '4',
      title: 'Development',
      description: 'Agile development with regular updates and transparent communication.'
    },
    {
      step: '5',
      title: 'Testing',
      description: 'Rigorous quality assurance to ensure performance, security, and reliability.'
    },
    {
      step: '6',
      title: 'Launch & Support',
      description: 'We deploy your solution and provide ongoing maintenance and support.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services - Versaimmortal Infotech</title>
        <meta name="description" content="Explore our comprehensive IT services including web development, mobile apps, cloud solutions, cybersecurity, digital marketing, and more." />
        <meta name="keywords" content="IT services, web development, mobile app development, cloud solutions, cybersecurity, digital marketing, UI/UX design" />
        <meta property="og:title" content="Services - Versaimmortal Infotech" />
        <meta property="og:description" content="We offer end-to-end technology solutions to accelerate your business growth." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://versaimmortal.com/services" />
        
        {/* JSON-LD structured data for services */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": services.map((service, index) => ({
              "@type": "Service",
              "position": index + 1,
              "name": service.title,
              "description": service.description,
              "provider": {
                "@type": "Organization",
                "name": "Versaimmortal Infotech"
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 text-white py-24">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto"
          >
            Comprehensive technology solutions tailored to your business needs.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Offer</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From strategy to execution, we deliver excellence across the entire technology spectrum.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300"
              >
                <div className="p-8">
                  <div className="mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <span className="text-primary-500 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A proven methodology to ensure your project's success.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line (hidden on mobile) */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-200"></div>

            <div className="space-y-8 md:space-y-0">
              {process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className={`flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                    <div className="bg-white p-6 rounded-lg shadow-lg">
                      <span className="text-4xl font-bold text-primary-200 mb-2 block">{step.step}</span>
                      <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                  <div className="md:w-1/2 flex justify-center my-4 md:my-0">
                    <div className="w-8 h-8 bg-primary-600 rounded-full border-4 border-white shadow z-10"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Versaimmortal Infotech?</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-primary-600 text-white rounded-full p-2 mr-4">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Experienced Team</h3>
                    <p className="text-gray-600">Our developers average 8+ years of experience in their respective domains.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-600 text-white rounded-full p-2 mr-4">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Client-Centric Approach</h3>
                    <p className="text-gray-600">We listen, collaborate, and adapt to ensure your vision becomes reality.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-600 text-white rounded-full p-2 mr-4">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Cutting-Edge Technology</h3>
                    <p className="text-gray-600">We stay ahead of the curve with the latest tools and frameworks.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-primary-600 text-white rounded-full p-2 mr-4">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">End-to-End Support</h3>
                    <p className="text-gray-600">From ideation to maintenance, we're with you every step of the way.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Team meeting"
                className="rounded-lg shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">Ready to start your project?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Let's discuss how our services can help you achieve your business goals.
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300 inline-flex items-center"
            >
              Get in Touch <FaArrowRight className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;